import { ReloadOutlined } from '@ant-design/icons'
import { Button, Input, Tag } from 'antd'
import { useState } from 'react'
import {
  THEME_PRESETS,
  isHexColor,
  useThemeStore,
  type ThemeSkinTokens,
} from '@/stores/theme.store'

type SkinFieldKey = keyof ThemeSkinTokens

type SkinFieldMeta = {
  key: SkinFieldKey
  label: string
}

const skinFieldMeta: SkinFieldMeta[] = [
  { key: 'primary', label: '主题色' },
  { key: 'pageBg', label: '页面背景' },
  { key: 'cardBg', label: '卡片背景' },
  { key: 'text', label: '文字色' },
]

const presetMeta = [
  { key: 'default-blue', label: '默认蓝' },
  { key: 'deep-ocean', label: '深海蓝' },
  { key: 'forest-green', label: '森林绿' },
  { key: 'warm-sand', label: '暖沙棕' },
] as const

const swatches: Record<SkinFieldKey, string[]> = {
  primary: ['#1677FF', '#1D4ED8', '#2F855A', '#B7791F', '#C2410C'],
  pageBg: ['#F5F5F5', '#EAF2FF', '#EEF7F1', '#FBF5EA', '#FFF7ED'],
  cardBg: ['#FFFFFF', '#F8FBFF', '#F8FCF8', '#FFFDF8', '#FFFBF5'],
  text: ['#1F1F1F', '#102A43', '#1F3D2D', '#4A3728', '#7C2D12'],
}

export default function DevToolsThemeSection() {
  const mode = useThemeStore((s) => s.mode)
  const activePreset = useThemeStore((s) => s.activePreset)
  const customSkin = useThemeStore((s) => s.customSkin)
  const setMode = useThemeStore((s) => s.setMode)
  const setPreset = useThemeStore((s) => s.setPreset)
  const updateCustomSkin = useThemeStore((s) => s.updateCustomSkin)
  const resetTheme = useThemeStore((s) => s.resetTheme)
  const [draftValues, setDraftValues] = useState<ThemeSkinTokens>(customSkin)
  const [errors, setErrors] = useState<Partial<Record<SkinFieldKey, string>>>({})

  const handleCustomChange = (key: SkinFieldKey, value: string) => {
    const nextValue = value.trim().toUpperCase()

    setDraftValues((prev) => ({
      ...prev,
      [key]: nextValue,
    }))

    if (!isHexColor(nextValue)) {
      setErrors((prev) => ({
        ...prev,
        [key]: '请输入 #RRGGBB 格式',
      }))
      return
    }

    setErrors((prev) => ({
      ...prev,
      [key]: undefined,
    }))
    updateCustomSkin({ [key]: nextValue })
  }

  const handleResetTheme = () => {
    resetTheme()
    setDraftValues(THEME_PRESETS['default-blue'])
    setErrors({})
  }

  return (
    <section className="rounded bg-white p-3 shadow-sm" data-c="DevToolsThemeSection">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <div className="text-sm font-medium text-gray-800">换肤</div>
          <div className="mt-0.5 text-xs text-gray-400">调试主题模式、预设方案和自定义颜色</div>
        </div>
        <Tag color={activePreset === 'custom' ? 'magenta' : 'blue'}>
          {activePreset === 'custom' ? 'CUSTOM' : 'PRESET'}
        </Tag>
      </div>

      <div className="space-y-4">
        <div>
          <div className="mb-2 text-xs font-medium text-gray-700">模式切换</div>
          <div className="grid grid-cols-2 gap-2">
            {(['light', 'dark'] as const).map((value) => {
              const isActive = mode === value
              return (
                <Button
                  key={value}
                  type={isActive ? 'primary' : 'default'}
                  onClick={() => setMode(value)}
                >
                  {value === 'light' ? '浅色' : '深色'}
                </Button>
              )
            })}
          </div>
        </div>

        <div>
          <div className="mb-2 text-xs font-medium text-gray-700">预设皮肤</div>
          <div className="grid gap-2">
            {presetMeta.map(({ key, label }) => {
              const skin = THEME_PRESETS[key]
              const isActive = activePreset === key
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => {
                    setPreset(key)
                    setDraftValues(customSkin)
                  }}
                  className={[
                    'rounded-lg border px-3 py-3 text-left transition-colors',
                    isActive
                      ? 'border-blue-400 bg-blue-50'
                      : 'border-gray-200 bg-gray-50 hover:bg-gray-100',
                  ].join(' ')}
                >
                  <span className="flex items-center justify-between gap-3">
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-gray-800">{label}</span>
                      <span className="mt-1 block text-xs text-gray-400">{skin.primary}</span>
                    </span>
                    <span className="flex gap-1.5">
                      {skinFieldMeta.map(({ key: fieldKey }) => (
                        <span
                          key={`${key}-${fieldKey}`}
                          className="h-5 w-5 rounded-full border border-white shadow-sm"
                          style={{ backgroundColor: skin[fieldKey] }}
                        />
                      ))}
                    </span>
                  </span>
                </button>
              )
            })}
          </div>
        </div>

        <div>
          <div className="mb-2 text-xs font-medium text-gray-700">自定义颜色</div>
          <div className="space-y-3">
            {skinFieldMeta.map(({ key, label }) => (
              <div key={key} className="rounded-lg bg-gray-50 px-3 py-3">
                <div className="mb-2 flex items-center justify-between">
                  <div className="text-xs font-medium text-gray-700">{label}</div>
                  <div
                    className="h-5 w-5 rounded-full border border-white shadow-sm"
                    style={{ backgroundColor: customSkin[key] }}
                  />
                </div>

                <div className="mb-2 flex flex-wrap gap-2">
                  {swatches[key].map((color) => (
                    <button
                      key={`${key}-${color}`}
                      type="button"
                      aria-label={`${label}-${color}`}
                      className="h-6 w-6 rounded-full border border-white shadow-sm"
                      style={{ backgroundColor: color }}
                      onClick={() => handleCustomChange(key, color)}
                    />
                  ))}
                </div>

                <Input
                  value={draftValues[key]}
                  onChange={(event) => handleCustomChange(key, event.target.value)}
                  placeholder="#RRGGBB"
                />

                {errors[key] ? (
                  <div className="mt-1 text-xs text-red-500">{errors[key]}</div>
                ) : null}
              </div>
            ))}
          </div>
        </div>

        <Button icon={<ReloadOutlined />} onClick={handleResetTheme}>
          恢复默认皮肤
        </Button>
      </div>
    </section>
  )
}
